/**
 * Shared legend for both audit pages — the app's first in-page legend.
 * Always shows the five vendor-status words on their pastels; "What do these
 * mean?" expands (1) the status translation — every raw Tekion status, Rapid
 * Recon step, and HomeNet state next to the color it maps to, served from the
 * classifier's own bucket maps so the legend can never drift — and (2) the
 * named problem types (label, meaning, action) from the backend registry.
 * Visual spec: the approved audit-remodel mockup artifact `.legend` (quiet
 * surface, 14px gaps, 12px secondary ink).
 *
 * The worklist no longer shows this strip (2026-09-28): its contents moved
 * into the Inventory Audit page's Help pop-up, which renders the same pieces
 * exported here — VendorStatusWords, StatusTranslation, ProblemMeanings — so
 * the two can never say different things. The strip itself remains for the
 * parked sheet view.
 */
import React, { useEffect, useState } from "react"

import { ProblemTypeInfo, StatusLegendResponse, getStatusLegend } from "../../api/audit"
import { THEME } from "../../config/theme"
import { useProblemTypes } from "../../hooks/useProblemTypes"

export const BUCKET_ORDER = [
  "online", "holding", "pipeline", "pending", "offline", "gone", "missing", "unknown",
] as const

const SEVERITY_COLORS = THEME.problemDisplay.severityColors as Record<string, string>

/** The vendor-status words on their pastels, in the legend's reading order. */
export function VendorStatusWords() {
  return (
    <>
      {BUCKET_ORDER.map((bucket) => {
        const cell = THEME.problemDisplay.bucketCells[bucket]
        return (
          <span
            key={bucket}
            style={{
              background: cell.background,
              color: THEME.problemDisplay.bucketCellText,
              borderRadius: 3,
              padding: "3px 8px 4px",
              fontSize: 11.5,
              fontWeight: 600,
            }}
          >
            {cell.label}
          </span>
        )
      })}
    </>
  )
}

/**
 * Each problem type's label (with its severity bar), meaning, and action.
 * `stacked` puts the meaning under the label instead of beside it — the Help
 * pop-up's narrower column, where a long label beside its meaning would crowd
 * a phone screen.
 */
export function ProblemMeanings({ types, stacked = false }: { types: ProblemTypeInfo[]; stacked?: boolean }) {
  return (
    <>
      {types.map((info) => (
        <div
          key={info.slug}
          style={{
            display: "flex",
            flexDirection: stacked ? "column" : "row",
            gap: stacked ? 2 : THEME.spacing.sm,
            padding: stacked ? "6px 0" : "2px 0",
          }}
        >
          <span
            style={{
              fontWeight: 600,
              color: THEME.colors.neutral.light.textPrimary,
              borderLeft: `${THEME.problemDisplay.chipAccentBarWidth}px solid ${
                SEVERITY_COLORS[info.severity] ?? THEME.colors.semantic.info
              }`,
              paddingLeft: 6,
              whiteSpace: stacked ? "normal" : "nowrap",
            }}
          >
            {info.label}
          </span>
          <span style={stacked ? { paddingLeft: 6 + THEME.problemDisplay.chipAccentBarWidth } : undefined}>
            {info.meaning} <em>Action: {info.action}</em>
          </span>
        </div>
      ))}
    </>
  )
}

export default function LegendStrip() {
  const registry = useProblemTypes()
  const [expanded, setExpanded] = useState(false)

  // The status translation loads lazily on first expand — it's reference
  // material, not something every page view needs to fetch.
  const [statusLegend, setStatusLegend] = useState<StatusLegendResponse | null>(null)
  useEffect(() => {
    if (!expanded || statusLegend !== null) return
    let cancelled = false
    getStatusLegend()
      .then((legend) => {
        if (!cancelled) setStatusLegend(legend)
      })
      .catch(() => {
        // The problem meanings below still render; the translation simply
        // stays in its loading state until a later expand succeeds.
      })
    return () => {
      cancelled = true
    }
  }, [expanded, statusLegend])

  const stripStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 14,
    background: THEME.problemDisplay.legendBackground,
    border: `1px solid ${THEME.colors.neutral.light.border}`,
    borderRadius: THEME.borderRadius.md,
    padding: "8px 14px",
    marginBottom: THEME.spacing.md,
    fontSize: 12,
    color: THEME.colors.neutral.light.textSecondary,
  }

  return (
    <div style={stripStyle}>
      <span style={{ fontWeight: 600, color: THEME.colors.neutral.light.textPrimary }}>
        Vendor status:
      </span>
      <VendorStatusWords />
      <button
        type="button"
        onClick={() => setExpanded((open) => !open)}
        style={{
          marginLeft: "auto",
          background: "none",
          border: "none",
          color: THEME.colors.semantic.info,
          cursor: "pointer",
          fontSize: 12,
          fontFamily: THEME.fonts.primary,
          padding: 0,
        }}
        aria-expanded={expanded}
      >
        {expanded ? "Hide problem meanings" : "What do these mean?"}
      </button>
      {expanded && (
        <div style={{ flexBasis: "100%", paddingTop: THEME.spacing.xs }}>
          <StatusTranslation legend={statusLegend} />
          {registry === null ? (
            <span>Loading problem meanings…</span>
          ) : (
            <ProblemMeanings types={Object.values(registry)} />
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Per-feed translation table: for each feed, one row per bucket — the bucket
 * word on its pastel, then every raw value that maps to it. Mirrors the
 * matrix cells' mental model (the fact in words, the interpretation in color).
 */
export function StatusTranslation({ legend }: { legend: StatusLegendResponse | null }) {
  if (legend === null) {
    return (
      <div style={{ paddingBottom: THEME.spacing.sm }}>Loading status translation…</div>
    )
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingBottom: THEME.spacing.sm }}>
      {legend.feeds.map((feed) => {
        const byBucket = new Map<string, string[]>()
        for (const entry of feed.entries) {
          const values = byBucket.get(entry.bucket) ?? []
          values.push(entry.value)
          byBucket.set(entry.bucket, values)
        }
        return (
          <div key={feed.key} style={{ display: "flex", gap: THEME.spacing.sm }}>
            <span
              style={{
                fontWeight: 600,
                color: THEME.colors.neutral.light.textPrimary,
                whiteSpace: "nowrap",
                minWidth: 92,
              }}
            >
              {feed.label}
            </span>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {BUCKET_ORDER.filter((bucket) => byBucket.has(bucket))
                .concat(
                  // Buckets outside the strip's display order (e.g. blank)
                  // still get their row rather than silently vanishing.
                  Array.from(byBucket.keys()).filter(
                    (bucket) => !(BUCKET_ORDER as readonly string[]).includes(bucket)
                  ) as (typeof BUCKET_ORDER)[number][]
                )
                .map((bucket) => {
                  const cell = THEME.problemDisplay.bucketCells[bucket] ?? {
                    background: THEME.colors.lightGray,
                    label: bucket,
                  }
                  return (
                    <div key={bucket} style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                      <span
                        style={{
                          background: cell.background,
                          color: THEME.problemDisplay.bucketCellText,
                          borderRadius: 3,
                          padding: "1px 7px 2px",
                          fontSize: 10.5,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {cell.label}
                      </span>
                      <span>{(byBucket.get(bucket) ?? []).join(", ")}</span>
                    </div>
                  )
                })}
            </div>
          </div>
        )
      })}
    </div>
  )
}
